import type { User } from '@interfaces/user';
import InfoDialog from '@ovComponents/2-component/infoDialog/infoDialog';
import { SelectionTile } from '@ovComponents/3-pattern/selectionTile/selectionTile';
import { additionalInfo } from '@ovComponents/4-module/configurableOptionFields/additionalInfo';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { InlineImage } from '@pages/ClientDetail/documents/inlineImage';
import { InlineImageLocal } from '@pages/ClientDetail/documents/inlineImageLocal';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { Box, Typography } from 'hephaestus/web';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLocalization } from '../../../../../hooks/useLocalization';
import { type FieldOptions, type FormErrors, validateFields } from '../utils';

export const RiskQuestionVisual = ({
  options,
  userData,
  loading,
  question,
  workflowCompletion,
  updateUser,
  continueFunc,
}: {
  options: any;
  userData: Partial<User>;
  loading: boolean;
  question: number;
  workflowCompletion?: any;
  updateUser: (user: Partial<User>) => void;
  continueFunc: () => void;
}) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation('client');
  const { sendAnalytic } = useContext(AnalyticsContext);
  const [focused, setFocused] = useState<string[]>([]);
  const [errors, setErrors] = useState<FormErrors>(null);
  const fieldOptions: FieldOptions = useMemo(
    () => ({
      riskQuestion1: { required: question === 1 },
      riskQuestion2: { required: question === 2 },
    }),
    [question],
  );

  const validate = useCallback(
    (candidateFields?: string[]): FormErrors => {
      const data = {
        riskQuestion1: userData.riskQuestion1,
        riskQuestion2: userData.riskQuestion2,
      };
      const newErrors = validateFields(fieldOptions, data, candidateFields);
      setErrors(newErrors);
      return newErrors;
    },
    [fieldOptions, userData],
  );

  const submit = async () => {
    const formErrors = validate();

    if (formErrors) {
      setFocused(Object.keys(formErrors));
      return;
    }

    if (!formErrors) {
      const analyticEvent =
        question === 1
          ? ovAnalyticsEvents.workflowsRiskQuestion1ContinueButtonSelect
          : ovAnalyticsEvents.workflowsRiskQuestion2ContinueButtonSelect;
      sendAnalytic(analyticEvent, {
        workflowStepTitle: options?.title,
        workflowId: workflowCompletion?.workflow?.id,
        workflowName: workflowCompletion?.workflow?.name,
        activeWorkflowCompletionId: workflowCompletion?.id,
        objectId: workflowCompletion?.objectId,
        objectType: workflowCompletion?.objectType,
      });
      await continueFunc();
    }
  };

  useEffect(() => {
    validate(focused);
  }, [validate, focused]);

  const questionField = question === 1 ? 'riskQuestion1' : 'riskQuestion2';

  return (
    <WorkflowCompletionForm
      onSubmit={submit}
      submitButtonProps={{
        label: t('continue'),
        disabled: loading,
        dataTestId: `risk-question-${question}-continue-button`,
      }}
    >
      {options?.title && (
        <Typography variant='displayLarge' sx={{ mt: 1, mb: 3 }}>
          {translateBackendString(options?.title)}
          <Box
            sx={{
              display: 'inline-flex',
            }}
          >
            {additionalInfo(
              translateBackendString,
              options?.title?.additionalInfo,
            ) && (
              <InfoDialog
                information={
                  additionalInfo(
                    translateBackendString,
                    options?.title?.additionalInfo,
                  ) ?? ''
                }
              />
            )}
          </Box>
        </Typography>
      )}
      {options?.subtitle && (
        <Typography
          variant='bodyLarge'
          component={'div'}
          sx={{
            mb: 3,
            table: { width: '100%' },
            display: 'flex',
            alignItems: 'end',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexDirection: 'column',
              m: 0,
              '& > *': { m: 0 },
            }}
          >
            <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
              {translateBackendString(options?.subtitle)}
            </ReactMarkdown>
          </Box>
          {additionalInfo(
            translateBackendString,
            options?.subtitle?.additionalInfo,
          ) && (
            <InfoDialog
              information={
                additionalInfo(
                  translateBackendString,
                  options?.subtitle?.additionalInfo,
                ) ?? ''
              }
            />
          )}
        </Typography>
      )}

      {options?.attachment?.file && (
        <Box sx={{ mb: '20px' }}>
          <InlineImageLocal
            data-testid='attachment'
            file={options?.attachment?.file}
            style={{
              objectFit: 'contain',
              height: '300px',
              borderRadius: '15px',
            }}
          />
        </Box>
      )}
      {options?.attachment?.value && (
        <Box sx={{ mb: '20px' }}>
          <InlineImage
            data-testid='attachment'
            fileDocumentId={options?.attachment?.value}
            style={{
              objectFit: 'contain',
              height: '300px',
              borderRadius: '15px',
            }}
          />
        </Box>
      )}

      <Box>
        <SelectionTile
          testId={`risk-question-${question}`}
          onChange={(e: any) =>
            updateUser({ ...userData, [questionField]: e.target.value })
          }
          value={userData[questionField] ?? ''}
          options={options?.options?.map((option: any) => ({
            value: option.value,
            label: translateBackendString(option.label),
            subtitle: translateBackendString(option.description),
          }))}
          error={errors?.riskQuestion1?.active || errors?.riskQuestion2?.active}
        />
      </Box>
    </WorkflowCompletionForm>
  );
};

export default RiskQuestionVisual;
